import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/api";
import { toast } from "sonner";
import { RefreshCw, Smartphone, CheckCircle2, XCircle, Clock, IndianRupee, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_LABELS = {
  pending: { label: "Pending Review", color: "#f59e0b" },
  reviewed: { label: "Offer Sent", color: "#3b82f6" },
  accepted: { label: "Accepted", color: "#22c55e" },
  rejected: { label: "Rejected", color: "#ef4444" },
  completed: { label: "Completed", color: "#8b5cf6" },
  cancelled: { label: "Cancelled", color: "#6b7280" },
};

const CONDITION_LABELS = {
  excellent: { label: "Excellent", icon: "✨" },
  good: { label: "Good", icon: "👍" },
  fair: { label: "Fair", icon: "🔧" },
  poor: { label: "Poor", icon: "⚠️" },
};

export default function AdminSellRequestManager({ query = "" }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [offerDraft, setOfferDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/api/admin/sell-requests")
      .then(({ data }) => setRequests(data))
      .catch((err) => toast.error(apiError(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = requests.filter((r) => {
    const matchesFilter = filter === "all" || r.status === filter;
    const q = query.toLowerCase();
    const matchesQuery = !q || r.model?.toLowerCase().includes(q) || r.brand?.toLowerCase().includes(q) || r.contact_phone?.includes(q);
    return matchesFilter && matchesQuery;
  });

  const sendOffer = async (id) => {
    const price = Number(offerDraft[id]);
    if (!price || price < 100) return toast.error("Valid offer price daalo (min ₹100)");
    setBusy(true);
    try {
      await api.post(`/api/admin/sell-requests/${id}/offer`, { offer_price: price });
      toast.success("Offer bheja!");
      setOfferDraft((d) => ({ ...d, [id]: "" }));
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const markReceived = async (id) => {
    setBusy(true);
    try {
      await api.post(`/api/admin/sell-requests/${id}/received`);
      toast.success("Device received mark ho gayi!");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const paySeller = async (id) => {
    const price = Number(offerDraft[id]);
    if (!price || price < 100) return toast.error("Payment amount enter karo");
    setBusy(true);
    try {
      await api.post(`/api/admin/sell-requests/${id}/pay`, { amount: price, note: "Wallet credit – sell request" });
      toast.success(`₹${price.toLocaleString("en-IN")} seller wallet mein credit ho gaye!`);
      setOfferDraft((d) => ({ ...d, [id]: "" }));
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const cancelRequest = async (id) => {
    if (!window.confirm("Is sell request ko cancel karna chahte hain?")) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/sell-requests/${id}/cancel`);
      toast.success("Request cancelled");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="sell-req-manager" data-testid="admin-sell-requests">
      {/* Header + Filters */}
      <div className="sell-req-header">
        <div className="sell-req-filters" data-testid="sell-req-filters">
          {["all", "pending", "reviewed", "accepted", "completed", "cancelled"].map((f) => (
            <button
              key={f}
              className={`sell-req-filter-btn${filter === f ? " on" : ""}`}
              onClick={() => setFilter(f)}
              data-testid={`sell-req-filter-${f}`}
            >
              {f === "all" ? "All" : STATUS_LABELS[f]?.label || f}
              {f !== "all" && (
                <span className="sell-req-count">{requests.filter((r) => r.status === f).length}</span>
              )}
            </button>
          ))}
        </div>
        <button className="admin-refresh-btn" onClick={load} data-testid="sell-req-refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {loading ? (
        <div className="empty-state"><Smartphone size={30} /><h3>Loading sell requests…</h3></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state" data-testid="sell-req-empty">
          <Smartphone size={40} />
          <h3>No sell requests found</h3>
          <p>{filter !== "all" ? `No ${STATUS_LABELS[filter]?.label} requests` : "Jab koi device sell karne ki request karega, yahan dikhega"}</p>
        </div>
      ) : (
        <div className="sell-req-list">
          {filtered.map((req) => {
            const isOpen = expanded === req.id;
            const s = STATUS_LABELS[req.status] || {};
            const cond = CONDITION_LABELS[req.condition] || {};
            return (
              <div key={req.id} className={`sell-req-card${isOpen ? " open" : ""}`} data-testid={`sell-req-${req.id}`}>
                {/* Card Header */}
                <div className="sell-req-card-head" onClick={() => setExpanded(isOpen ? null : req.id)}>
                  <div className="sell-req-device-info">
                    <Smartphone size={16} style={{ color: "#00d4ff" }} />
                    <div>
                      <b data-testid={`sell-req-model-${req.id}`}>{req.brand} {req.model}</b>
                      <small>{req.storage} · {req.color} · {cond.icon} {cond.label}</small>
                    </div>
                  </div>
                  <div className="sell-req-card-meta">
                    {req.admin_offer > 0 && (
                      <span className="sell-req-offer-pill" data-testid={`sell-req-offer-${req.id}`}>
                        <IndianRupee size={11} /> {Number(req.admin_offer).toLocaleString("en-IN")}
                      </span>
                    )}
                    <span className="sell-req-status-dot" style={{ color: s.color, borderColor: s.color + "44", background: s.color + "15" }} data-testid={`sell-req-status-${req.id}`}>
                      {s.label || req.status}
                    </span>
                    <small className="sell-req-date">{new Date(req.created_at).toLocaleDateString("en-IN")}</small>
                    {isOpen ? <ChevronUp size={14} style={{ color: "#6b7f9e" }} /> : <ChevronDown size={14} style={{ color: "#6b7f9e" }} />}
                  </div>
                </div>

                {/* Expanded Details */}
                {isOpen && (
                  <div className="sell-req-card-body">
                    <div className="sell-req-details-grid">
                      <div className="sell-req-detail-block">
                        <span className="sell-req-detail-label">Contact</span>
                        <b>{req.contact_phone || "—"}</b>
                      </div>
                      <div className="sell-req-detail-block">
                        <span className="sell-req-detail-label">Condition</span>
                        <b>{cond.icon} {cond.label}</b>
                      </div>
                      <div className="sell-req-detail-block">
                        <span className="sell-req-detail-label">Issues</span>
                        <b>{req.issues?.length ? req.issues.join(", ") : "None"}</b>
                      </div>
                      {req.description && (
                        <div className="sell-req-detail-block" style={{ gridColumn: "1/-1" }}>
                          <span className="sell-req-detail-label">Customer Notes</span>
                          <b>{req.description}</b>
                        </div>
                      )}
                    </div>

                    {/* Timeline */}
                    {req.timeline?.length > 0 && (
                      <div className="sell-req-timeline">
                        <span className="sell-req-detail-label">Timeline</span>
                        {req.timeline.slice(-3).map((t, i) => (
                          <div key={i} className="sell-req-timeline-item">
                            <i />
                            <span>{t.label}</span>
                            <time>{new Date(t.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</time>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    {["pending", "under_review"].includes(req.status) && (
                      <div className="sell-req-actions" data-testid={`sell-req-actions-${req.id}`}>
                        <div className="sell-req-offer-row">
                          <label>Make Offer (₹)</label>
                          <input
                            type="number"
                            placeholder="e.g. 25000"
                            value={offerDraft[req.id] || ""}
                            onChange={(e) => setOfferDraft((d) => ({ ...d, [req.id]: e.target.value }))}
                            className="sell-req-offer-input"
                            data-testid={`sell-req-offer-input-${req.id}`}
                          />
                          <button
                            className="sell-req-action-btn primary"
                            onClick={() => sendOffer(req.id)}
                            disabled={busy}
                            data-testid={`sell-req-send-offer-${req.id}`}
                          >
                            <IndianRupee size={13} /> Send Offer
                          </button>
                        </div>
                        <button
                          className="sell-req-action-btn danger"
                          onClick={() => cancelRequest(req.id)}
                          disabled={busy}
                          data-testid={`sell-req-cancel-${req.id}`}
                        >
                          <XCircle size={13} /> Reject Request
                        </button>
                      </div>
                    )}

                    {["accepted", "in_transit", "store_visit"].includes(req.status) && (
                      <div className="sell-req-actions">
                        <button
                          className="sell-req-action-btn success"
                          onClick={() => markReceived(req.id)}
                          disabled={busy}
                          data-testid={`sell-req-mark-received-${req.id}`}
                        >
                          <CheckCircle2 size={13} /> Mark Device Received
                        </button>
                        <button className="sell-req-action-btn danger" onClick={() => cancelRequest(req.id)} disabled={busy}>
                          <XCircle size={13} /> Cancel
                        </button>
                      </div>
                    )}

                    {req.status === "received" && (
                      <div className="sell-req-actions" data-testid={`sell-req-pay-actions-${req.id}`}>
                        <div className="sell-req-offer-row">
                          <label>Pay Seller (₹)</label>
                          <input
                            type="number"
                            placeholder={`Offer was ₹${req.offer_price || ""}`}
                            defaultValue={req.offer_price || ""}
                            onChange={(e) => setOfferDraft((d) => ({ ...d, [req.id]: e.target.value }))}
                            className="sell-req-offer-input"
                            data-testid={`sell-req-pay-input-${req.id}`}
                          />
                          <button
                            className="sell-req-action-btn success"
                            onClick={() => paySeller(req.id)}
                            disabled={busy}
                            data-testid={`sell-req-pay-btn-${req.id}`}
                          >
                            <IndianRupee size={13} /> Pay & Complete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
